import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Box, Paper, Typography, Stack, Chip, IconButton, Tooltip, Button, TextField,
  InputAdornment, Avatar, Tab, Tabs, Snackbar, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, useTheme,
} from '@mui/material';
import {
  Save as SaveIcon, NoteAddOutlined, Delete, Download, ContentCopy,
  Search as SearchIcon, Clear as ClearIcon, EditNote, History, Campaign,
  Drafts as DraftsIcon, PublishedWithChangesOutlined, Visibility, AutoAwesome,
  Edit, Unpublished,
} from '@mui/icons-material';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useAuth } from './AuthContext';
import { API } from '../apiProperties';
import { logAudit } from '../utils/audit';

interface Props { isActive: boolean }

type Status = 'draft' | 'published';

type ReleaseNote = {
  id: string;
  version: string;
  title: string;
  body: string;            // markdown
  highlights: string[];    // short bullet points for the spotlight summary
  status: Status;
  createdAt: number;
  updatedAt: number;
  publishedAt?: number;
  updatedBy?: string;
  publishedBy?: string;
};

// All persistence is server-side now. These helpers narrow the fetch surface
// so the component stays focused on UI state.
async function apiFetchAll(): Promise<ReleaseNote[]> {
  const resp = await fetch(API.release_notes, { credentials: 'include' });
  if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
  return resp.json();
}

async function apiCreate(payload: Partial<ReleaseNote>): Promise<ReleaseNote> {
  const resp = await fetch(API.release_notes, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) throw new Error(`Create failed: ${resp.status}`);
  return resp.json();
}

async function apiUpdate(id: string, payload: Partial<ReleaseNote>): Promise<ReleaseNote> {
  const resp = await fetch(`${API.release_notes}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) throw new Error(`Update failed: ${resp.status}`);
  return resp.json();
}

async function apiDelete(id: string): Promise<void> {
  const resp = await fetch(`${API.release_notes}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!resp.ok) throw new Error(`Delete failed: ${resp.status}`);
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Minimal markdown renderer — same as before, intentionally small.
const renderMarkdown = (raw: string): string => {
  if (!raw) return '<p><em>(empty)</em></p>';
  const lines = raw.split(/\r?\n/);
  const out: string[] = [];
  let inUl = false;
  let inOl = false;
  let inCode = false;
  let codeBuffer: string[] = [];
  const closeLists = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  };
  const inline = (s: string): string =>
    escapeHtml(s)
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/_([^_]+)_/g, '<em>$1</em>')
      // Render the rendered HTML via dangerouslySetInnerHTML, so a markdown
      // link href must be scheme-checked — only http(s)/mailto/relative are
      // allowed (blocks javascript:/data:), and quotes are escaped so the URL
      // can't break out of the attribute. Unsafe links degrade to plain text.
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text: string, rawHref: string) => {
        const href = rawHref.trim();
        if (!/^(https?:\/\/|mailto:|\/|#)/i.test(href)) return text;
        return `<a href="${href.replace(/"/g, '%22')}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      });
  for (const line of lines) {
    if (line.trim().startsWith('```')) {
      if (inCode) { out.push(`<pre><code>${escapeHtml(codeBuffer.join('\n'))}</code></pre>`); inCode = false; codeBuffer = []; }
      else { closeLists(); inCode = true; }
      continue;
    }
    if (inCode) { codeBuffer.push(line); continue; }
    if (/^###\s+/.test(line)) { closeLists(); out.push(`<h3>${inline(line.replace(/^###\s+/, ''))}</h3>`); continue; }
    if (/^##\s+/.test(line)) { closeLists(); out.push(`<h2>${inline(line.replace(/^##\s+/, ''))}</h2>`); continue; }
    if (/^#\s+/.test(line)) { closeLists(); out.push(`<h1>${inline(line.replace(/^#\s+/, ''))}</h1>`); continue; }
    const olMatch = line.match(/^\s*\d+\.\s+(.*)/);
    const ulMatch = line.match(/^\s*[-*]\s+(.*)/);
    if (olMatch) { if (inUl) { out.push('</ul>'); inUl = false; } if (!inOl) { out.push('<ol>'); inOl = true; } out.push(`<li>${inline(olMatch[1])}</li>`); continue; }
    if (ulMatch) { if (inOl) { out.push('</ol>'); inOl = false; } if (!inUl) { out.push('<ul>'); inUl = true; } out.push(`<li>${inline(ulMatch[1])}</li>`); continue; }
    closeLists();
    if (line.trim() === '') out.push('');
    else out.push(`<p>${inline(line)}</p>`);
  }
  closeLists();
  if (inCode) out.push(`<pre><code>${escapeHtml(codeBuffer.join('\n'))}</code></pre>`);
  return out.join('\n');
};

// DOMPurify config for markdown HTML — ADD_ATTR keeps target="_blank" on our
// scheme-checked links. DOMPurify.sanitize() is called inline at each
// dangerouslySetInnerHTML site so the sanitizer is visible at the injection point.
const MARKDOWN_SANITIZE_CFG = { ADD_ATTR: ['target'] };

export const ReleaseNotesComponent: React.FC<Props> = ({ isActive }) => {
  const { userInfo } = useAuth();
  const theme = useTheme();
  const [list, setList] = useState<ReleaseNote[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({ open: false, msg: '', severity: 'success' });

  // Editor draft state (separate from list so unsaved edits don't pollute storage)
  const [draftVersion, setDraftVersion] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftHighlights, setDraftHighlights] = useState('');
  const [editorTab, setEditorTab] = useState<0 | 1>(0);

  useEffect(() => {
    if (!isActive) return;
    apiFetchAll()
      .then(setList)
      .catch(() => setSnack({ open: true, msg: 'Failed to load release notes.', severity: 'error' }));
  }, [isActive]);

  const upsertLocal = (note: ReleaseNote) => {
    setList(prev => {
      const idx = prev.findIndex(n => n.id === note.id);
      if (idx === -1) return [note, ...prev];
      const copy = prev.slice();
      copy[idx] = note;
      return copy;
    });
  };

  const removeLocal = (id: string) => setList(prev => prev.filter(n => n.id !== id));

  // ---------- Derived views ----------
  const published = useMemo(() => list.filter(n => n.status === 'published').sort((a, b) => (b.publishedAt || 0) - (a.publishedAt || 0)), [list]);
  const drafts = useMemo(() => list.filter(n => n.status === 'draft').sort((a, b) => b.updatedAt - a.updatedAt), [list]);
  const spotlight = published[0];
  const archive = published.slice(1);

  const filteredArchive = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return archive;
    return archive.filter(n =>
      n.title.toLowerCase().includes(q) ||
      n.version.toLowerCase().includes(q) ||
      n.body.toLowerCase().includes(q) ||
      n.highlights.some(h => h.toLowerCase().includes(q)),
    );
  }, [archive, search]);

  // ---------- Editor actions ----------
  const openEditor = (note?: ReleaseNote) => {
    if (note) {
      setEditingId(note.id);
      setDraftVersion(note.version);
      setDraftTitle(note.title);
      setDraftBody(note.body);
      setDraftHighlights(note.highlights.join('\n'));
    } else {
      setEditingId(null);
      setDraftVersion('');
      setDraftTitle('');
      setDraftBody('');
      setDraftHighlights('');
    }
    setEditorTab(0);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
  };

  const persistFromDraft = async (status: Status): Promise<ReleaseNote | null> => {
    if (!draftVersion.trim()) {
      setSnack({ open: true, msg: 'Version is required.', severity: 'error' });
      return null;
    }
    if (!draftTitle.trim()) {
      setSnack({ open: true, msg: 'Title is required.', severity: 'error' });
      return null;
    }
    const userId = userInfo?.name || userInfo?.email || userInfo?.preferred_username;
    const highlights = draftHighlights
      .split(/\r?\n/)
      .map(s => s.replace(/^\s*[-*]\s*/, '').trim())
      .filter(Boolean);
    const payload = {
      version: draftVersion.trim(),
      title: draftTitle.trim(),
      body: draftBody,
      highlights,
      status,
      updatedBy: userId,
      publishedBy: status === 'published' ? userId : undefined,
    };
    setBusy(true);
    try {
      const saved = editingId ? await apiUpdate(editingId, payload) : await apiCreate(payload);
      upsertLocal(saved);
      return saved;
    } catch (e) {
      setSnack({ open: true, msg: 'Save failed. Try again.', severity: 'error' });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleSaveDraft = async () => {
    const n = await persistFromDraft('draft');
    if (n) {
      setSnack({ open: true, msg: 'Draft saved.', severity: 'success' });
      logAudit({
        action: 'save_release_note_draft',
        targetType: 'release_note',
        targetId: n.id,
        metadata: { version: n.version, title: n.title },
      });
      closeEditor();
    }
  };

  const handlePublish = async () => {
    const n = await persistFromDraft('published');
    if (n) {
      setSnack({ open: true, msg: `Published v${n.version}.`, severity: 'success' });
      logAudit({
        action: 'publish_release_note',
        targetType: 'release_note',
        targetId: n.id,
        metadata: { version: n.version, title: n.title },
      });
      closeEditor();
    }
  };

  // Publish a draft straight from the list row. Mirrors the editor's
  // handlePublish: a busy guard stops rapid double-clicks firing duplicate
  // API calls, and the action is recorded in the audit log.
  const publishDraft = async (d: ReleaseNote) => {
    if (busy) return;
    setBusy(true);
    try {
      const userId = userInfo?.name || userInfo?.email || userInfo?.preferred_username;
      const saved = await apiUpdate(d.id, { status: 'published', publishedBy: userId });
      upsertLocal(saved);
      setSnack({ open: true, msg: `Published v${d.version}.`, severity: 'success' });
      logAudit({
        action: 'publish_release_note',
        targetType: 'release_note',
        targetId: d.id,
        metadata: { version: d.version, title: d.title },
      });
    } catch {
      setSnack({ open: true, msg: 'Failed to publish.', severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleUnpublish = async (id: string) => {
    try {
      const saved = await apiUpdate(id, { status: 'draft' });
      upsertLocal(saved);
      setSnack({ open: true, msg: 'Moved to drafts.', severity: 'info' });
    } catch {
      setSnack({ open: true, msg: 'Failed to unpublish.', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const target = list.find(n => n.id === id);
      await apiDelete(id);
      removeLocal(id);
      setSnack({ open: true, msg: 'Note deleted.', severity: 'info' });
      logAudit({
        action: 'delete_release_note',
        targetType: 'release_note',
        targetId: id,
        metadata: target ? { version: target.version, title: target.title, status: target.status } : undefined,
      });
    } catch {
      setSnack({ open: true, msg: 'Failed to delete.', severity: 'error' });
    }
  };

  const downloadMarkdown = (n: ReleaseNote) => {
    const heading = `# FS App Manager — ${n.title}\n_${n.status === 'published' && n.publishedAt ? `Published ${format(new Date(n.publishedAt), 'PPpp')}` : `Draft · updated ${format(new Date(n.updatedAt), 'PPpp')}`} · v${n.version}_\n\n`;
    const hl = n.highlights.length ? `## Highlights\n\n${n.highlights.map(h => `- ${h}`).join('\n')}\n\n` : '';
    const blob = new Blob([heading + hl + n.body], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fsam-release-notes-v${n.version}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const copyMarkdown = (n: ReleaseNote) => {
    navigator.clipboard?.writeText(n.body)
      .then(() => setSnack({ open: true, msg: 'Markdown copied.', severity: 'success' }))
      .catch(() => setSnack({ open: true, msg: 'Copy failed.', severity: 'error' }));
  };

  const previewHtml = useMemo(() => renderMarkdown(draftBody), [draftBody]);
  const isDark = theme.palette.mode === 'dark';

  const markdownSx = {
    fontFamily: 'inherit',
    '& h1': { fontSize: '1.6rem', fontWeight: 800, mt: 0, mb: 1.5 },
    '& h2': { fontSize: '1.25rem', fontWeight: 700, mt: 2, mb: 1 },
    '& h3': { fontSize: '1.05rem', fontWeight: 700, mt: 1.5, mb: 0.75 },
    '& p': { mb: 1, lineHeight: 1.65 },
    '& ul, & ol': { pl: 3, mb: 1 },
    '& li': { mb: 0.4 },
    '& code': { px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: 'rgba(99, 102, 241, 0.12)', fontFamily: 'monospace', fontSize: '0.85em' },
    '& pre': { bgcolor: '#0f172a', color: '#e2e8f0', p: 1.5, borderRadius: 1.5, overflow: 'auto', fontSize: '0.8rem' },
    '& pre code': { bgcolor: 'transparent', p: 0 },
    '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
  } as const;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' }}>
              <Campaign sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Release Notes</Typography>
              <Typography variant="body2" color="textSecondary">
                What's new in FS App Manager — a chronological feed of platform changes.
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Button
          data-tour="new-release"
          startIcon={<NoteAddOutlined />}
          variant="contained"
          onClick={() => openEditor()}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          New release
        </Button>
      </Stack>

      {/* Spotlight — latest published */}
      {spotlight ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'rgba(99, 102, 241, 0.25)',
            background: isDark
              ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(139, 92, 246, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(139, 92, 246, 0.04) 100%)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.18) 0%, transparent 70%)' }} />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
            <Chip
              size="small"
              icon={<AutoAwesome sx={{ fontSize: 14 }} />}
              label="Latest release"
              sx={{ fontWeight: 700, bgcolor: 'rgba(99, 102, 241, 0.18)', color: '#4338ca', '& .MuiChip-icon': { color: '#4338ca' } }}
            />
            <Chip size="small" label={`v${spotlight.version}`} sx={{ fontWeight: 700, fontFamily: 'monospace', bgcolor: 'rgba(15, 23, 42, 0.08)' }} />
            {spotlight.publishedAt && (
              <Typography variant="caption" color="text.secondary">
                Published {format(new Date(spotlight.publishedAt), 'PPP')} · {formatDistanceToNowStrict(spotlight.publishedAt, { addSuffix: true })}
                {spotlight.publishedBy ? ` by ${spotlight.publishedBy}` : ''}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Edit" arrow><IconButton size="small" onClick={() => openEditor(spotlight)}><Edit fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Move back to draft" arrow><IconButton size="small" onClick={() => handleUnpublish(spotlight.id)}><Unpublished fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Copy markdown" arrow><IconButton size="small" onClick={() => copyMarkdown(spotlight)}><ContentCopy fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Download .md" arrow><IconButton size="small" onClick={() => downloadMarkdown(spotlight)}><Download fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Delete" arrow><IconButton size="small" onClick={() => handleDelete(spotlight.id)} sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton></Tooltip>
          </Stack>
          <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em', mb: 1.5, color: 'text.primary', lineHeight: 1.1 }}>
            {spotlight.title}
          </Typography>
          {spotlight.highlights.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 2,
                bgcolor: isDark ? 'rgba(15, 23, 42, 0.45)' : 'rgba(255, 255, 255, 0.65)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: '0.08em' }}>Highlights</Typography>
              <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                {spotlight.highlights.map((h, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.85, flexShrink: 0 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{h}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
          <Box sx={{ ...markdownSx, '& h1': { display: 'none' } }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(spotlight.body), MARKDOWN_SANITIZE_CFG) }} />
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            mb: 4,
            borderRadius: 4,
            border: '2px dashed',
            borderColor: 'divider',
            textAlign: 'center',
            background: isDark ? 'rgba(99, 102, 241, 0.05)' : 'rgba(99, 102, 241, 0.03)',
          }}
        >
          <Campaign sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>No release notes yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click <strong>New release</strong> to write the first one — once published, it'll headline this page.
          </Typography>
          <Button variant="contained" startIcon={<NoteAddOutlined />} onClick={() => openEditor()} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Write the first release
          </Button>
        </Paper>
      )}

      {/* Drafts */}
      {drafts.length > 0 && (
        <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
            <DraftsIcon sx={{ color: '#f59e0b' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Drafts</Typography>
            <Chip size="small" label={`${drafts.length} draft${drafts.length !== 1 ? 's' : ''}`} sx={{ fontWeight: 700, bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#92400e' }} />
          </Stack>
          <Stack spacing={1}>
            {drafts.map(d => (
              <Stack
                key={d.id}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                spacing={1.25}
                sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                    <Chip size="small" label={`v${d.version}`} sx={{ fontWeight: 700, fontFamily: 'monospace', bgcolor: 'rgba(15, 23, 42, 0.06)' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{d.title}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Updated {formatDistanceToNowStrict(d.updatedAt, { addSuffix: true })}
                    {d.updatedBy ? ` by ${d.updatedBy}` : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Edit" arrow><IconButton size="small" onClick={() => openEditor(d)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Publish" arrow>
                    <span>
                      <IconButton size="small" disabled={busy} onClick={() => publishDraft(d)} sx={{ color: 'success.main' }}>
                        <PublishedWithChangesOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete" arrow><IconButton size="small" onClick={() => handleDelete(d.id)} sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Archive timeline of older published */}
      {archive.length > 0 && (
        <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
            <History sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>Previous releases</Typography>
            <Chip size="small" label={`${archive.length} release${archive.length !== 1 ? 's' : ''}`} sx={{ fontWeight: 700 }} />
          </Stack>
          <TextField
            size="small"
            placeholder="Search by version, title or content"
            value={search}
            onChange={e => setSearch(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Stack spacing={1.25}>
            {filteredArchive.map(n => <TimelineEntry key={n.id} note={n} onView={() => openEditor(n)} onUnpublish={() => handleUnpublish(n.id)} onDelete={() => handleDelete(n.id)} onCopy={() => copyMarkdown(n)} onDownload={() => downloadMarkdown(n)} />)}
            {filteredArchive.length === 0 && (
              <Typography variant="caption" color="text.secondary">No previous releases match "{search}".</Typography>
            )}
          </Stack>
        </Paper>
      )}

      {/* Editor dialog */}
      <Dialog
        open={editorOpen}
        onClose={closeEditor}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' }}>
              <EditNote sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {editingId ? 'Edit release notes' : 'New release notes'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Notes about FS App Manager itself — not per-app.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Version"
                size="small"
                placeholder="1.3.2"
                value={draftVersion}
                onChange={e => setDraftVersion(e.target.value)}
                sx={{ width: { sm: 180 } }}
                InputProps={{ startAdornment: <InputAdornment position="start">v</InputAdornment> }}
              />
              <TextField
                label="Release title"
                size="small"
                placeholder="e.g. Build Cost Dashboard + Dark Mode"
                value={draftTitle}
                onChange={e => setDraftTitle(e.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              label="Highlights (one per line)"
              size="small"
              placeholder={'- Build cost dashboard now live\n- Dark mode toggle in the header\n- Deploy Scheduler'}
              value={draftHighlights}
              onChange={e => setDraftHighlights(e.target.value)}
              multiline
              minRows={3}
              helperText="Optional. Shown as bullet points in the spotlight card."
            />
            <Tabs value={editorTab} onChange={(_, v) => setEditorTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Write" sx={{ textTransform: 'none', fontWeight: 700 }} />
              <Tab label="Preview" icon={<Visibility sx={{ fontSize: 16 }} />} iconPosition="end" sx={{ textTransform: 'none', fontWeight: 700 }} />
            </Tabs>
            {editorTab === 0 ? (
              <TextField
                multiline
                minRows={14}
                fullWidth
                placeholder={'## What\'s new\n\n- Feature A\n- Bug fix B\n\n## Known issues\n\n1. ...'}
                value={draftBody}
                onChange={e => setDraftBody(e.target.value)}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem', alignItems: 'flex-start' } }}
              />
            ) : (
              <Box
                sx={{
                  minHeight: 380,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2.5,
                  bgcolor: 'background.default',
                  ...markdownSx,
                }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml, MARKDOWN_SANITIZE_CFG) }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeEditor} color="inherit">Cancel</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleSaveDraft} disabled={busy} startIcon={<SaveIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Save as draft
          </Button>
          <Button onClick={handlePublish} disabled={busy} variant="contained" startIcon={<PublishedWithChangesOutlined />} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {editingId && list.find(n => n.id === editingId)?.status === 'published' ? 'Save changes' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

const TimelineEntry: React.FC<{
  note: ReleaseNote;
  onView: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onDownload: () => void;
}> = ({ note, onView, onUnpublish, onDelete, onCopy, onDownload }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ position: 'relative', pl: 3, borderLeft: '2px solid', borderColor: 'divider' }}>
      <Avatar
        sx={{
          position: 'absolute', left: -11, top: 8, width: 20, height: 20,
          bgcolor: 'primary.main', color: '#fff', fontSize: '0.7rem', fontWeight: 800,
        }}
      >
        ●
      </Avatar>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          transition: 'all 150ms ease',
          '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(99, 102, 241, 0.04)' },
        }}
        onClick={() => setOpen(v => !v)}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Chip size="small" label={`v${note.version}`} sx={{ fontWeight: 700, fontFamily: 'monospace', bgcolor: 'rgba(15, 23, 42, 0.06)' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>{note.title}</Typography>
          {note.publishedAt && (
            <Typography variant="caption" color="text.secondary">
              {format(new Date(note.publishedAt), 'PP')}
            </Typography>
          )}
          <Tooltip title="Edit" arrow><IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(); }}><Edit fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Move to draft" arrow><IconButton size="small" onClick={(e) => { e.stopPropagation(); onUnpublish(); }}><Unpublished fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Copy markdown" arrow><IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopy(); }}><ContentCopy fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Download .md" arrow><IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownload(); }}><Download fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Delete" arrow><IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ color: 'error.main' }}><Delete fontSize="small" /></IconButton></Tooltip>
        </Stack>
        {note.highlights.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1, rowGap: 0.5 }}>
            {note.highlights.slice(0, 4).map((h, i) => (
              <Chip key={i} size="small" label={h.length > 50 ? h.slice(0, 47) + '…' : h} sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(99, 102, 241, 0.08)', color: '#4338ca' }} />
            ))}
            {note.highlights.length > 4 && (
              <Typography variant="caption" color="text.secondary">+{note.highlights.length - 4} more</Typography>
            )}
          </Stack>
        )}
        {open && (
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              '& h1': { fontSize: '1.2rem', fontWeight: 800 },
              '& h2': { fontSize: '1.05rem', fontWeight: 700 },
              '& h3': { fontSize: '0.95rem', fontWeight: 700 },
              '& p': { mb: 1, lineHeight: 1.6 },
              '& ul, & ol': { pl: 3, mb: 1 },
              '& li': { mb: 0.4 },
              '& code': { px: 0.5, borderRadius: 0.5, bgcolor: 'rgba(99, 102, 241, 0.12)', fontFamily: 'monospace', fontSize: '0.85em' },
              '& pre': { bgcolor: '#0f172a', color: '#e2e8f0', p: 1.5, borderRadius: 1.5, overflow: 'auto', fontSize: '0.8rem' },
              '& a': { color: 'primary.main', textDecoration: 'none' },
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(note.body), MARKDOWN_SANITIZE_CFG) }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ReleaseNotesComponent;
